import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Star, UserCheck, ExternalLink, Search, Plus, Handshake, Trash2, Archive, RotateCcw } from "lucide-react";

const ROLES = [
  "Officer", "Rifleman", "Assault", "Automatic Rifleman", "Medic", "Support",
  "Machine Gunner", "Anti-Tank", "Engineer", "Spotter", "Sniper",
  "Tank Commander", "Crewman", "Commander",
];
const GAMES = ["Hell Let Loose"];

export default function FreeAgents() {
  const { user } = useAuth();
  const isAuthed = user && user !== false;
  const [agents, setAgents] = useState([]);
  const [myTeams, setMyTeams] = useState([]);
  const [watched, setWatched] = useState(new Set());
  const [listOpen, setListOpen] = useState(false);
  const [form, setForm] = useState({ handle: "", real_name: "", role: ROLES[0], country: "US", game: GAMES[0], hll_profile_url: "", bio: "", hll_kills: null, hll_deaths: null, hll_matches: null, hll_winrate: null });
  const [hllLookup, setHllLookup] = useState(null);
  const [signFor, setSignFor] = useState(null);
  const [signTeam, setSignTeam] = useState("");
  const [q, setQ] = useState("");

  const load = () => {
    api.get("/free-agents").then((r) => setAgents(r.data));
    if (isAuthed) {
      api.get("/me/teams").then((r) => setMyTeams(r.data));
      api.get("/me/watchlist").then((r) => setWatched(new Set(r.data)));
    }
  };
  useEffect(() => { load(); }, [isAuthed]); // eslint-disable-line

  const verifyHll = async () => {
    if (!form.hll_profile_url) return;
    try {
      const { data } = await api.get(`/hll/lookup?query=${encodeURIComponent(form.hll_profile_url)}`);
      setHllLookup(data);
      if (data.exists) {
        setForm((f) => ({ ...f, hll_profile_url: data.url }));
        toast.success("HLL Records profile verified.");
      } else {
        toast.error("Couldn't verify that profile — you can still list, but the link may 404.");
      }
    } catch {
      toast.error("HLL lookup failed");
    }
  };

  const submitList = async () => {
    if (!form.handle) { toast.error("Handle is required."); return; }
    try {
      await api.post("/free-agents", form);
      toast.success(`${form.handle} listed on the market.`);
      setListOpen(false);
      setForm({ handle: "", real_name: "", role: ROLES[0], country: "US", game: GAMES[0], hll_profile_url: "", bio: "", hll_kills: null, hll_deaths: null, hll_matches: null, hll_winrate: null });
      setHllLookup(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const sign = async () => {
    if (!signTeam) { toast.error("Pick a team."); return; }
    try {
      await api.post(`/free-agents/${signFor.id}/pickup?team_id=${signTeam}`);
      toast.success(`${signFor.handle} signed.`);
      setSignFor(null);
      setSignTeam("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to sign");
    }
  };

  const toggleWatch = async (e, p) => {
    e.preventDefault(); e.stopPropagation();
    if (!isAuthed) { toast.error("Log in to watch free agents."); return; }
    const isW = watched.has(p.id);
    try {
      if (isW) {
        await api.delete(`/free-agents/${p.id}/watch`);
        setWatched((prev) => { const n = new Set(prev); n.delete(p.id); return n; });
      } else {
        await api.post(`/free-agents/${p.id}/watch`);
        setWatched((prev) => new Set(prev).add(p.id));
        toast.success(`Watching ${p.handle} — we'll ping if another team signs them.`);
      }
    } catch (er) { toast.error(er.response?.data?.detail || "Failed"); }
  };

  const retire = async (p) => {
    if (!window.confirm(`Withdraw ${p.handle} from the free-agent list?`)) return;
    try {
      await api.post(`/free-agents/${p.id}/retire`);
      toast.success(`${p.handle} withdrawn.`);
      load();
    } catch (er) { toast.error(er.response?.data?.detail || "Failed"); }
  };

  const removePost = async (p) => {
    if (!window.confirm(`Delete ${p.handle} permanently? This cannot be undone.`)) return;
    try {
      await api.delete(`/free-agents/${p.id}`);
      toast.success(`${p.handle} removed.`);
      load();
    } catch (er) { toast.error(er.response?.data?.detail || "Failed"); }
  };

  const filtered = agents.filter((p) => {
    if (!q) return true;
    const t = q.toLowerCase();
    return p.handle?.toLowerCase().includes(t)
      || p.real_name?.toLowerCase().includes(t)
      || p.game?.toLowerCase().includes(t)
      || p.role?.toLowerCase().includes(t);
  });

  return (
    <div className="space-y-8" data-testid="free-agents-root">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-cyan-400 font-mono tracking-[0.3em] text-xs mb-2">/ MARKET · NON-COMPETING</div>
          <h1 className="font-heading text-4xl lg:text-5xl font-black tracking-tighter uppercase">Free Agents</h1>
          <p className="text-gray-400 mt-2 max-w-xl text-sm">
            Unsigned players open to offers. Verified via <a className="text-cyan-400 hover:underline" href="https://hllrecords.com" target="_blank" rel="noreferrer">HLL Records</a> when a profile link is provided.
          </p>
        </div>
        {isAuthed && (
          <Dialog open={listOpen} onOpenChange={setListOpen}>
            <DialogTrigger asChild>
              <Button data-testid="list-fa-btn"
                      className="rounded-none bg-cyan-400 hover:bg-cyan-300 text-black uppercase tracking-widest font-bold">
                <Plus className="w-4 h-4 mr-2" /> List a free agent
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#101015] border-[#232330] text-white rounded-none max-w-lg">
              <DialogHeader><DialogTitle className="font-heading uppercase tracking-tight">List on the market</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-gray-400">Handle</Label>
                    <Input data-testid="fa-handle" value={form.handle} onChange={(e) => setForm({ ...form, handle: e.target.value })}
                           className="rounded-none bg-[#05050A] border-[#232330] mt-2 font-mono" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-gray-400">Real name</Label>
                    <Input value={form.real_name} onChange={(e) => setForm({ ...form, real_name: e.target.value })}
                           className="rounded-none bg-[#05050A] border-[#232330] mt-2" />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-gray-400">Role</Label>
                    <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                      <SelectTrigger className="rounded-none bg-[#05050A] border-[#232330] mt-2"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#101015] border-[#232330] text-white">
                        {ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-gray-400">Game</Label>
                    <Select value={form.game} onValueChange={(v) => setForm({ ...form, game: v })}>
                      <SelectTrigger className="rounded-none bg-[#05050A] border-[#232330] mt-2"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#101015] border-[#232330] text-white">
                        {GAMES.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-gray-400">CO</Label>
                    <Input maxLength={2} value={form.country}
                           onChange={(e) => setForm({ ...form, country: e.target.value.toUpperCase() })}
                           className="rounded-none bg-[#05050A] border-[#232330] mt-2 uppercase font-mono text-center" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-gray-400 flex items-center justify-between">
                    <span>HLL Records profile <span className="text-gray-600 normal-case">(link, hash, or community slug)</span></span>
                    {hllLookup?.exists && <span className="text-cyan-400 normal-case">✓ verified</span>}
                  </Label>
                  <div className="flex gap-2 mt-2">
                    <Input data-testid="fa-hll" value={form.hll_profile_url}
                           onChange={(e) => { setForm({ ...form, hll_profile_url: e.target.value }); setHllLookup(null); }}
                           placeholder="hllrecords.com/profiles/… or a community slug"
                           className="rounded-none bg-[#05050A] border-[#232330] font-mono text-xs" />
                    <Button onClick={verifyHll} data-testid="fa-hll-verify"
                            variant="outline"
                            className="rounded-none border-[#232330] hover:border-cyan-400 shrink-0">
                      <Search className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-gray-400">Notes</Label>
                  <Textarea rows={3} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })}
                            placeholder="Availability, preferred role, playstyle…"
                            className="rounded-none bg-[#05050A] border-[#232330] mt-2" />
                </div>
                <div className="border-t border-[#232330] pt-3">
                  <div className="text-[10px] font-mono tracking-widest text-gray-500 uppercase mb-2">
                    Career (optional — used when HLL Records is shield-protected)
                  </div>
                  <div className="grid grid-cols-4 gap-2">
                    <div>
                      <Label className="text-[9px] font-mono text-gray-500 uppercase">Kills</Label>
                      <Input type="number" min={0} value={form.hll_kills ?? ""}
                             data-testid="fa-manual-kills"
                             onChange={(e) => setForm({ ...form, hll_kills: e.target.value === "" ? null : Number(e.target.value) })}
                             className="rounded-none bg-[#05050A] border-[#232330] mt-1 font-mono" />
                    </div>
                    <div>
                      <Label className="text-[9px] font-mono text-gray-500 uppercase">Deaths</Label>
                      <Input type="number" min={0} value={form.hll_deaths ?? ""}
                             onChange={(e) => setForm({ ...form, hll_deaths: e.target.value === "" ? null : Number(e.target.value) })}
                             className="rounded-none bg-[#05050A] border-[#232330] mt-1 font-mono" />
                    </div>
                    <div>
                      <Label className="text-[9px] font-mono text-gray-500 uppercase">Matches</Label>
                      <Input type="number" min={0} value={form.hll_matches ?? ""}
                             onChange={(e) => setForm({ ...form, hll_matches: e.target.value === "" ? null : Number(e.target.value) })}
                             className="rounded-none bg-[#05050A] border-[#232330] mt-1 font-mono" />
                    </div>
                    <div>
                      <Label className="text-[9px] font-mono text-gray-500 uppercase">Win%</Label>
                      <Input type="number" min={0} max={100} value={form.hll_winrate ?? ""}
                             onChange={(e) => setForm({ ...form, hll_winrate: e.target.value === "" ? null : Number(e.target.value) })}
                             className="rounded-none bg-[#05050A] border-[#232330] mt-1 font-mono" />
                    </div>
                  </div>
                </div>
                <Button onClick={submitList} data-testid="fa-submit"
                        className="w-full rounded-none bg-cyan-400 hover:bg-cyan-300 text-black uppercase tracking-widest font-bold">
                  List on market
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="flex items-center gap-2 border border-[#232330] bg-[#101015] px-3 py-2">
        <Search className="w-4 h-4 text-gray-500 shrink-0" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Filter by handle, game, or role"
          data-testid="fa-filter"
          className="w-full bg-transparent outline-none text-sm placeholder:text-gray-600"
        />
        <span className="text-[10px] font-mono text-gray-500 tracking-widest shrink-0">{filtered.length} available</span>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {filtered.map((p) => (
          <div
            key={p.id}
            data-testid={`fa-card-${p.handle}`}
            className="border border-[#232330] bg-[#101015] p-5 flex flex-col"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="font-mono text-[10px] tracking-widest text-gray-500">{p.country || "??"}</div>
              <div className="flex items-center gap-2">
                {isAuthed && (
                  <button
                    onClick={(e) => toggleWatch(e, p)}
                    data-testid={`watch-${p.handle}`}
                    className={`transition-colors ${watched.has(p.id) ? "text-[#F4D03F]" : "text-gray-600 hover:text-[#F4D03F]"}`}
                    aria-label={watched.has(p.id) ? "Unwatch" : "Watch"}
                  >
                    <Star className={`w-4 h-4 ${watched.has(p.id) ? "fill-current" : ""}`} />
                  </button>
                )}
                <div className="font-mono text-[10px] tracking-widest text-cyan-400">{(p.role || "").toUpperCase()}</div>
              </div>
            </div>
            <div className="font-heading text-xl font-black uppercase tracking-tight">{p.handle}</div>
            {p.real_name && <div className="text-xs text-gray-500 mt-1">{p.real_name}</div>}
            <div className="text-xs text-[#FF007F] font-mono mt-2">{p.game}</div>
            {p.bio && <p className="text-xs text-gray-400 mt-3 line-clamp-3">{p.bio}</p>}
            <div className="mt-4 pt-4 border-t border-[#232330] flex items-center gap-2">
              {p.hll_profile_url && (
                <a
                  href={p.hll_profile_url}
                  target="_blank" rel="noreferrer"
                  data-testid={`fa-hll-link-${p.handle}`}
                  className="flex-1 flex items-center justify-center gap-2 text-[10px] font-mono uppercase tracking-widest text-cyan-400 border border-cyan-400 hover:bg-cyan-400 hover:text-black py-1.5 transition-colors"
                >
                  <ExternalLink className="w-3 h-3" /> HLL Records
                </a>
              )}
              {isAuthed && myTeams.length > 0 && (
                <Button
                  size="sm"
                  onClick={() => { setSignFor(p); setSignTeam(myTeams[0].id); }}
                  data-testid={`fa-sign-${p.handle}`}
                  className="rounded-none bg-[#FF007F] hover:bg-pink-400 text-white uppercase tracking-widest text-[10px] font-bold flex-1"
                >
                  <Handshake className="w-3 h-3 mr-1" /> Sign
                </Button>
              )}
            </div>
            {p.hll_stats && p.hll_stats.source !== "pending" && (
              <div
                className="mt-3 border border-cyan-400/30 bg-cyan-400/5 p-3"
                data-testid={`fa-career-${p.handle}`}
              >
                <div className="text-[9px] font-mono tracking-widest text-cyan-400 uppercase mb-2 flex items-center justify-between">
                  <span>HLL Career</span>
                  <span className="text-gray-500">
                    {p.hll_stats.source === "manual" ? "manual" : "synced"}
                  </span>
                </div>
                <div className="grid grid-cols-4 gap-2 text-center">
                  <div>
                    <div className="text-[9px] text-gray-500 font-mono">Kills</div>
                    <div className="font-mono font-bold text-white">{p.hll_stats.kills ?? "—"}</div>
                  </div>
                  <div>
                    <div className="text-[9px] text-gray-500 font-mono">K/D</div>
                    <div className="font-mono font-bold text-[#F4D03F]">{p.hll_stats.kd ?? "—"}</div>
                  </div>
                  <div>
                    <div className="text-[9px] text-gray-500 font-mono">MP</div>
                    <div className="font-mono font-bold text-white">{p.hll_stats.matches ?? "—"}</div>
                  </div>
                  <div>
                    <div className="text-[9px] text-gray-500 font-mono">Win%</div>
                    <div className="font-mono font-bold text-cyan-400">
                      {p.hll_stats.winrate != null ? Number(p.hll_stats.winrate).toFixed(0) + "%" : "—"}
                    </div>
                  </div>
                </div>
              </div>
            )}
            {p.hll_stats && p.hll_stats.source === "pending" && isAuthed && (
              <button
                onClick={async (e) => {
                  e.preventDefault();
                  try {
                    await api.post(`/players/${p.id}/hll-refresh`);
                    toast("Refresh queued — HLL Records is currently shield-protected. Add manual stats or try again later.");
                    load();
                  } catch (er) { toast.error(er.response?.data?.detail || "Failed"); }
                }}
                data-testid={`fa-career-refresh-${p.handle}`}
                className="mt-3 w-full text-[10px] font-mono tracking-widest uppercase border border-[#232330] hover:border-cyan-400 hover:text-cyan-400 text-gray-500 py-1.5 transition-colors"
              >
                Retry HLL career sync
              </button>
            )}
            {isAuthed && (user?.role === "admin" || p.listed_by === user?.id) && (
              <div className="mt-3 pt-3 border-t border-[#232330] flex gap-2">
                <button
                  onClick={() => retire(p)}
                  data-testid={`fa-retire-${p.handle}`}
                  className="flex-1 text-[10px] font-mono tracking-widest uppercase border border-[#232330] hover:border-[#F4D03F] hover:text-[#F4D03F] text-gray-500 py-1.5 flex items-center justify-center gap-1"
                >
                  <Archive className="w-3 h-3" /> Withdraw
                </button>
                <button
                  onClick={() => removePost(p)}
                  data-testid={`fa-remove-${p.handle}`}
                  className="flex-1 text-[10px] font-mono tracking-widest uppercase border border-[#232330] hover:border-red-500 hover:text-red-500 text-gray-500 py-1.5 flex items-center justify-center gap-1"
                >
                  <Trash2 className="w-3 h-3" /> Delete
                </button>
              </div>
            )}
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="col-span-full border border-[#232330] p-10 text-center text-gray-500">
            <UserCheck className="w-10 h-10 mx-auto text-gray-600 mb-3" />
            No free agents listed yet.
          </div>
        )}
      </div>

      <Dialog open={!!signFor} onOpenChange={(o) => !o && setSignFor(null)}>
        <DialogContent className="bg-[#101015] border-[#232330] text-white rounded-none">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-tight">
              Sign {signFor?.handle}
            </DialogTitle>
          </DialogHeader>
          <Label className="text-xs uppercase tracking-widest text-gray-400 mt-2">Choose your team</Label>
          <Select value={signTeam} onValueChange={setSignTeam}>
            <SelectTrigger data-testid="sign-team-select" className="rounded-none bg-[#05050A] border-[#232330]">
              <SelectValue placeholder="Pick a team" />
            </SelectTrigger>
            <SelectContent className="bg-[#101015] border-[#232330] text-white">
              {myTeams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} · {t.tag}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button
            onClick={sign}
            data-testid="confirm-sign"
            className="rounded-none bg-cyan-400 hover:bg-cyan-300 text-black uppercase tracking-widest font-bold"
          >
            Lock signing
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
